import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Search,
  TvMinimalPlay,
} from "lucide-react";
import { buildCourseUnitLookup } from "@/service/course.mappers";
import { selectCurrentCourseCount } from "@/store/slices/courseSlice";
import { useSelector } from "react-redux";
import { CourseCompleteCheckSVG } from "@/utils/svg";
import { selectUserRole } from "@/store/slices/authSlice";
import { ROLES } from "@/utils/constants";

export interface UnitItem {
  id: string;
  name: string;
  is_active?: boolean;
  status?: string;
  is_completed?: boolean;
  duration?: string; // UI: show (mm:ss) in syllabus
  notify: {
    assignment: number;
    query: number;
  };
}

export interface LessonItem {
  id: string;
  name: string;
  is_active?: boolean;
  status?: string;
  units: UnitItem[];
  isExpanded?: boolean;
}

interface StudyListProps {
  title: string;
  lessons: LessonItem[];
  activeLesson?: string;
  onUnitSelect: (lesson: LessonItem, unit: UnitItem) => void;
  selectedTab: string;
  showProgress?: boolean;
  gradeTitle?: string;
  termTabs?: string[];
  terms?: { name: string; lessons: LessonItem[] }[];
}

const StudyList = ({
  title,
  lessons,
  activeLesson,
  onUnitSelect,
  selectedTab,
  showProgress,
  gradeTitle,
  termTabs = ["Term 1", "Term 2", "Term 3", "Term 4"],
  terms,
}: StudyListProps) => {
  // Initialize with an empty object first
  const [expandedUnits, setExpandedUnits] = useState<Record<string, boolean>>(
    {}
  );

  const courseCount = useSelector(selectCurrentCourseCount);
  const userRole = useSelector(selectUserRole);

  const [searchQuery, setSearchQuery] = useState("");
  const computedTabs = (terms?.map((t) => t.name) || termTabs).filter(Boolean);
  const [selectedTerm, setSelectedTerm] = useState(
    computedTabs[0] || "Term 1"
  );
  const tabsRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [hasTermOverflow, setHasTermOverflow] = useState(false);

  const updateTermScrollState = useCallback(() => {
    const el = tabsRef.current;
    if (!el) return;

    const { scrollLeft, scrollWidth, clientWidth } = el;
    const overflow = scrollWidth - clientWidth > 1;

    setHasTermOverflow(overflow);
    setCanScrollLeft(overflow && scrollLeft > 1);
    setCanScrollRight(overflow && scrollLeft + clientWidth < scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = tabsRef.current;
    if (!el) return;

    updateTermScrollState();

    el.addEventListener("scroll", updateTermScrollState, { passive: true });
    const resizeObserver = new ResizeObserver(updateTermScrollState);
    resizeObserver.observe(el);

    return () => {
      el.removeEventListener("scroll", updateTermScrollState);
      resizeObserver.disconnect();
    };
  }, [computedTabs, updateTermScrollState]);

  const scrollTabs = (dir: "left" | "right") => {
    const el = tabsRef.current;
    if (!el) return;
    if (dir === "left" && !canScrollLeft) return;
    if (dir === "right" && !canScrollRight) return;
    el.scrollBy({ left: dir === "left" ? -160 : 160, behavior: "smooth" });
  };

  const lessonsForTerm = useMemo(() => {
    return terms?.find((t) => t.name === selectedTerm)?.lessons ?? lessons;
  }, [terms, selectedTerm, lessons]);

  const courseUnitById = useMemo(() => {
    if (!courseCount) return new Map<string, never>();
    return buildCourseUnitLookup(courseCount);
  }, [courseCount]);

  // Expand all lessons shown for the current term when term or lesson data changes
  useEffect(() => {
    const initialExpandedState = lessonsForTerm.reduce((acc, lesson) => {
      acc[lesson.id] = true;
      return acc;
    }, {} as Record<string, boolean>);

    setExpandedUnits(initialExpandedState);
  }, [lessonsForTerm]);

  // Filter lessons and units based on search query
  const filteredLessons = useMemo(() => {
    const termLessons =
      terms?.find((t) => t.name === selectedTerm)?.lessons || lessons;

    if (!searchQuery.trim()) {
      return termLessons; // Return selected term lessons if no search query
    }

    const query = searchQuery.toLowerCase().trim();

    return termLessons
      .filter((lesson) => {
        // Check if lesson name matches
        const lessonMatches = lesson.name.toLowerCase().includes(query);

        // Filter units that match the search query
        const matchingUnits = lesson.units.filter(
          (unit) =>
            unit.name.toLowerCase().includes(query) ||
            (unit.status && unit.status.toLowerCase().includes(query))
        );

        // Include this lesson if either the lesson name matches or any of its units match
        return lessonMatches || matchingUnits.length > 0;
      })
      .map((lesson) => {
        // If search query matches lesson name, include all units
        if (lesson.name.toLowerCase().includes(query)) {
          return lesson;
        }

        // Otherwise, only include matching units
        return {
          ...lesson,
          units: lesson.units.filter(
            (unit) =>
              unit.name.toLowerCase().includes(query) ||
              (unit.status && unit.status.toLowerCase().includes(query))
          ),
        };
      });
  }, [lessons, searchQuery, selectedTerm, terms]);

  const toggleUnit = (unitId: string) => {
    setExpandedUnits((prev) => ({
      ...prev,
      [unitId]: !prev[unitId],
    }));
  };

  // Expand all units that have matching search results
  useEffect(() => {
    if (searchQuery.trim()) {
      const newExpandedState = { ...expandedUnits };
      filteredLessons.forEach((lesson: LessonItem) => {
        newExpandedState[lesson.id] = true;
      });
      setExpandedUnits(newExpandedState);
    }
  }, [searchQuery, filteredLessons]);
  const showProgressPercent = showProgress && (userRole?.code === ROLES.STUDENT || userRole?.code === ROLES.SUPER_ADMIN || userRole?.code === ROLES.TEACHER);
  return (
    <div className="flex flex-col w-full bg-[#FFFFFF] rounded-lg overflow-hidden lg:h-[55rem] h-[45rem]">
      {/* Search bar - Fixed header */}
      <div
        className={`px-[16px] ${
          userRole?.code === ROLES.STUDENT
            ? "pt-[24px]"
            : "py-[24px] border-b border-gray-100"
        } mb-0 `}
      >
        <h2 className="text-lg font-medium text-[#1F1F1F] mb-2">
          {gradeTitle || title}
        </h2>

        <div className="mb-4 flex items-center gap-2">
          {hasTermOverflow ? (
            <button
              type="button"
              className="group flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#F6F7FA] transition-colors enabled:hover:bg-[#5C88C5] disabled:cursor-default disabled:opacity-40"
              onClick={() => scrollTabs("left")}
              disabled={!canScrollLeft}
              aria-label="Scroll terms left"
            >
              <ChevronLeft
                className="h-4 w-4 text-[#1C1B1F] transition-colors group-hover:text-white"
                strokeWidth={2}
              />
            </button>
          ) : null}
          <div
            ref={tabsRef}
            className="scrollbar-hide flex min-h-0 flex-1 items-center gap-2 overflow-x-auto overflow-y-hidden"
          >
            {computedTabs.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setSelectedTerm(t)}
                className={`h-[38px] min-w-[67px] shrink-0 whitespace-nowrap rounded-[10px] border bg-white px-4 transition-colors ${
                  selectedTerm === t
                    ? "border-[#5C88C5] text-[14px] font-semibold text-[#5C88C5]"
                    : "border-[#CCCCCC] text-[12px] font-medium text-[#4A5661]"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
          {hasTermOverflow ? (
            <button
              type="button"
              className="group flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#F6F7FA] transition-colors enabled:hover:bg-[#5C88C5] disabled:cursor-default disabled:opacity-40"
              onClick={() => scrollTabs("right")}
              disabled={!canScrollRight}
              aria-label="Scroll terms right"
            >
              <ChevronRight
                className="h-4 w-4 text-[#1C1B1F] transition-colors group-hover:text-white"
                strokeWidth={2}
              />
            </button>
          ) : null}
        </div>

        <div className="relative w-full h-[48px] flex items-center space-x-2">
          <input
            type="text"
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-[24px] pr-[56px] w-full h-full placeholder:text-borderBlack py-[14px] border border-[#E5E7EB] rounded-[12px] text-label2a-d text-borderBlack focus:outline-none"
          />
          <div className="absolute inset-y-0 right-[56px] mx-3 flex items-center pointer-events-none">
            <div className="h-5 w-[1px] bg-[#E5E7EB]"></div>
          </div>

          <div className="absolute top-[7px] right-[24px] w-[32px] h-[32px] rounded-[100px] flex items-center justify-center bg-[#5C88C5] p-[4px] cursor-pointer hover:bg-[#4B71A6]">
            <Search className="w-[20px] h-[20px] text-white" />
          </div>
        </div>
      </div>

      {/* Course Progress */}
      {showProgressPercent && (
        <div className="px-[16px] my-[12px]">
          <div className="bg-[#DEEAF7] p-[8px] rounded-[12px]">
            <h2 className="text-[14px] font-semibold text-[#2E2E32] mb-2">
              Course Progress
            </h2>

            {/* Course completion progress bar */}
            <div className="flex flex-col w-full">
              <div className="w-full bg-[#CFCFCF] rounded-full h-2.5 mb-2">
                <div
                  className="bg-[#795FA9] h-2.5 rounded-full transition-all duration-300 ease-in-out"
                  style={{ width: courseCount?.course_completion || "0%" }}
                ></div>
              </div>
              <div className="flex justify-start items-center">
                <span className="text-[18px] font-semibold text-[#2E2E32] mr-[2px]">
                  {courseCount?.course_completion || "0%"}
                </span>
                <span className="text-[12px] font-semibold text-[#2E2E32]">
                  Completed
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Unit content - Scrollable area */}
      <div className="flex flex-col overflow-y-auto flex-grow">
        {filteredLessons.map((lesson) => {
          const isUnitActive = lesson.units.some(
            (unit) => unit.id === activeLesson
          );

          return (
            <div key={lesson.id} className="block">
              {/* Unit header */}
              <div
                className={`pl-[16px] pr-[24px] py-5 cursor-pointer h-[64px] flex items-center justify-between ${
                  isUnitActive ? "bg-[#FB6567] group" : ""
                }`}
                onClick={() => toggleUnit(lesson.id)}
              >
                <h2
                  className={`text-xl font-bold  ${
                    isUnitActive ? "text-white" : "text-[#2E2E32]"
                  }`}
                >
                  {lesson.name}
                </h2>
                <div className="w-[24px] h-[24px] flex items-center justify-center">
                  {expandedUnits[lesson.id] ? (
                    <ChevronUp
                      className={`text-[#2E2E32] ${
                        isUnitActive ? "text-white" : ""
                      }`}
                    />
                  ) : (
                    <ChevronDown
                      className={`text-[#2E2E32] ${
                        isUnitActive ? "text-white" : ""
                      }`}
                    />
                  )}
                </div>
              </div>

              {/* Lessons list */}
              <div
                className={`overflow-hidden transition-all duration-300 ease-in-out ${
                  expandedUnits[lesson.id]
                    ? "max-h-[1000px] opacity-100"
                    : "max-h-0 opacity-0"
                }`}
              >
                {lesson.units.map((unit) => {
                  const isActive = activeLesson === unit.id;
                  const courseUnit = courseUnitById.get(unit.id);
                  const duration = unit.duration || courseUnit?.duration;
                  const assignmentCount = courseUnit?.notify?.assignment ?? 0;
                  const queryCount = courseUnit?.notify?.query ?? 0;
                  const isCompleted = courseUnit?.is_completed ?? false;

                  return (
                    <div
                      key={unit.id}
                      className={` ${isActive ? "bg-[#F0F4F9]" : ""}`}
                    >
                      <button
                        className="flex flex-row items-center justify-between w-full h-[64px] border py-[12px] px-[16px] border-gray-100"
                        onClick={() => onUnitSelect(lesson, unit)}
                      >
                        <div className="flex items-center space-x-2">
                          {userRole?.code === ROLES.STUDENT && isCompleted && (
                            <CourseCompleteCheckSVG />
                          )}

                          <div className="flex items-center justify-center w-5 h-5 rounded-full bg-gray-100">
                            <TvMinimalPlay className="text-[#2E2E32]" />
                          </div>

                          <div className="flex flex-col gap-1 items-start">
                            <span className="text-sm font-normal text-[#2E2E32]">
                              {unit.name}
                            </span>
                            {duration ? (
                              <span className="text-[12px] text-[#7B7B7B]">
                                {duration}
                              </span>
                            ) : null}
                          </div>
                        </div>
                        {selectedTab === "assignment" && assignmentCount > 0 && (
                            <div className="w-[30px] h-[30px] rounded-full flex items-center justify-center bg-[#5C88C5] text-white transition-all duration-300">
                              {assignmentCount}
                            </div>
                          )}
                        {selectedTab === "feedback" && queryCount > 0 && (
                            <div className="w-[30px] h-[30px] rounded-full flex items-center justify-center bg-[#5C88C5] text-white transition-all duration-300">
                              {queryCount}
                            </div>
                          )}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default StudyList;
